<template>
  <div v-if="campaignProduct">
    <StepTitle hr>
      <div>{{title}}</div>
      <template #subtitle>{{subtitle}}</template>
      <template #product>
        <table class="selected-item-table selected-item">
          <tr>
            <td><img :src="getImageUrl" alt=""></td>
            <td class="product-name">{{quantity}} &times; <span v-html="campaignProduct.name"></span></td>
          </tr>
        </table>
      </template>
    </StepTitle>
    <div>
      <section class="boxes select-item-table">
        <div class="index-content centered-block ">
          <b-row class="justify-content-md-center boxes select-item-btn customer-verify">
            <b-col lg="12" cols="12" class="pl-sm-3 pr-sm-3 pl-0 pr-0">
              <div class="order-it">
                <table>
                  <thead>
                    <tr>
                      <th v-for="name of Object.keys(variants)" :key="'column-' + name">{{name.toLowerCase().replace(/\w\S*/g, (w) => (w.replace(/^\w/, (c) => c.toUpperCase())))}}</th>
                      <th v-if="expectedQuantity > 1">Quantity</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="rowId of Object.keys(rows)" :key="'row-' + rowId">
                      <td v-for="[name, options] of Object.entries(variants)" :key="'cell-' + rowId + '-' + name">
                        <b-form-select v-model="rows[rowId][name]" required>
                          <option :value="null">-- Please Select --</option>
                          <option v-for="value in options" :key="'variant-' + rowId + '-' + name + '-' + value" :value="value">{{value}}</option>
                        </b-form-select>
                      </td>
                      <td v-if="expectedQuantity > 1">
                        <b-form-input
                          type="number"
                          v-model.trim="rows[rowId].quantity"
                          min="1"
                          :max="expectedQuantity"
                          required
                        ></b-form-input>
                      </td>
                      <td>
                        <b-link v-if="Object.keys(rows).length > 1" @click="deleteRow(rowId)"><font-awesome-icon :icon="['fas', 'times']"></font-awesome-icon></b-link>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </b-col>
          </b-row>
        </div>
      </section>
    </div>
    <b-row v-if="isBundledVariant" class="justify-content-md-center boxes select-item-btn customer-verify">
      <b-col md="4" cols="6">
        <b-button block @click="addRow()" class="btn btn-default btn-account font-weight-normal select-item-continue-btn" variant="primary">Add Another Option
          <font-awesome-icon :icon="['fas', 'plus']" class="footer_btn"></font-awesome-icon>
        </b-button>
      </b-col>
    </b-row>
    <b-row class="justify-content-md-center boxes select-item-btn customer-verify">
      <b-col v-if="session && session.can_undo" md="4" cols="6" class="back">
        <b-button block @click="goBack()" class="btn-outline-success btn-back stroke" variant="outline-primary">Back</b-button>
      </b-col>
      <b-col md="4" cols="6">
        <b-button block @click="done()" class="btn btn-default btn-account font-weight-normal select-item-continue-btn" variant="primary">Continue
          <font-awesome-icon :icon="['fas', 'caret-right']" class="footer_btn"></font-awesome-icon>
        </b-button>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import axios from 'axios'
import { mapState } from 'vuex'
import { getCampaignProductImageUrl } from '../utils'
import { VariantType } from '../constants'

const StepTitle = () => import('./StepTitle')

export default {
  name: 'Variants',
  components: {StepTitle},
  props: {
    item: Object,
    newCampaignProduct: Object,
    quantity: {
      type: Number,
      default: function() {
        return 1
      }
    }
  },
  data: function() {
    return {
      variants: {},
      idMap: {},
      rows: {},
      rowId: 1,
      confirmSelection: false,
      campaignProduct: null
    }
  },
  computed: {
    ...mapState(['session']),
    expectedQuantity: function() {
      return this.quantity * this.campaignProduct.bundle_quantity
    },
    getImageUrl: function() {
      return getCampaignProductImageUrl(this.campaignProduct, 'small')
    },
    title: function() {
      return this.confirmSelection ? 'Confirm Your Selection' : 'Choose Your Options'
    },
    subtitle: function() {
      return (this.expectedQuantity > 1) ? 'Total quantity of selections must equal ' + this.expectedQuantity.toString()
        : ''
    },
    isBundledVariant: function() {
      return this.campaignProduct.product.variant_type === VariantType.Bundled
    }
  },
  methods: {
    done: function() {
      let quantities = {}
      let totalQuantity = 0
      let duplicateRows = []
      this.confirmSelection = false
      this.$store.commit('clear_messages')

      for (const [rowId, row] of Object.entries(this.rows)) {
        // validate that all fields are filled out
        let pairs = []

        for (const name of Object.keys(this.variants)) {
          if (!row[name]) {
            this.$store.commit('error', 'Please fill out all fields')
            return
          }

          pairs.push(name + '=' + row[name])
        }

        const quantity = parseInt(row.quantity)
        const id = this.idMap[pairs.sort().join(',')]

        // validate quantities are filled out
        if (quantity <= 0) {
          this.$store.commit('error', 'Please fill out all quantities')
          return
        }

        if (id in quantities) {
          quantities[id].quantity += quantity
          duplicateRows.push(rowId) // delete the duplicate row
        } else {
          quantities[id] = {quantity: quantity, rowId: rowId}
        }

        totalQuantity += quantity
      }

      if (duplicateRows.length) {
        // delete duplicate rows
        duplicateRows.forEach(rowId => {
          if (rowId in this.rows) {
            delete this.rows[rowId]
          }
        })

        // update quantities
        Object.values(quantities).forEach(row => {
          if (row.rowId in this.rows) {
            this.rows[row.rowId].quantity = row.quantity
          }
        })

        this.rows = Object.assign({}, this.rows)
      }

      if (totalQuantity !== this.expectedQuantity) {
        this.$store.commit('error', 'Total quantity must be ' + this.expectedQuantity.toString())
        return
      }

      if (duplicateRows.length) {
        this.confirmSelection = true
        console.log('duplicate rows')
      } else {
        // done selecting variants
        let selections = []

        for (const [id, row] of Object.entries(quantities)) {
          selections.push({id: id, quantity: row.quantity})
        }

        this.$emit('select', {variants: selections})
      }
    },
    goBack: function() {
      this.$emit('back') // go back to the previous step (ignore selections)
    },
    deleteRow: function(rowId) {
      if (rowId in this.rows) {
        delete this.rows[rowId]
        this.rows = Object.assign({}, this.rows)
      }
    },
    addRow: function() {
      this.rowId++
      this.rows[this.rowId] = {quantity: 1}
      Object.keys(this.variants).forEach(key => {
        this.rows[this.rowId][key] = null
      })
      this.rows = Object.assign({}, this.rows)
    }
  },
  created() {
    this.$store.commit('busy')
    const inputCampaignProduct = this.newCampaignProduct || this.item.campaign_product

    axios.all([
      axios.get('/order_items/' + this.item.id.toString() + '/'),
      axios.get('/campaign_products/' + inputCampaignProduct.id.toString() + '/')
    ]).then(results => {
      // build unique set of variant options and map them to their ids
      console.log(results)
      let item = results[0].data
      this.campaignProduct = results[1].data

      this.campaignProduct.product.variants.forEach(variant => {
        let pairs = []

        variant.options.forEach(option => {
          if (!(option.name in this.variants)) {
            this.variants[option.name] = {}
          }
          this.variants[option.name][option.value] = true

          pairs.push(option.name + '=' + option.value)
        })

        this.idMap[pairs.sort().join(',')] = variant.id
      })

      // convert variants to sorted lists
      Object.keys(this.variants).forEach(key => {
        this.variants[key] = Object.keys(this.variants[key]).sort()
      })

      // get initial selected variants
      if (item.product.id === this.campaignProduct.product.id) {
        console.log(item)
        if (item.variant && item.variant.options) {
          this.rows[this.rowId] = {quantity: this.quantity}
          item.variant.options.forEach(option => {
            this.rows[this.rowId][option.name] = option.value
          })
        } else if (item.bundled_variants) {
          item.bundled_variants.forEach(bundledVariant => {
            this.rows[this.rowId] = {quantity: bundledVariant.quantity}
            bundledVariant.variant.options.forEach(option => {
              this.rows[this.rowId][option.name] = option.value
            })
            this.rowId++
          })
        }
      } else {
        this.addRow() // add a blank row
      }

      this.$store.commit('ready')
    }, error => {
      console.error(error)
      this.$store.commit('critical_error', error)
      this.$store.commit('ready')
    })
  }
}
</script>

<style scoped>
.stroke{
  border: 2px solid;
}

</style>
